import { FeatureCollection } from 'geojson'
import { useMemo } from 'react'
import { Layer, Source } from 'react-map-gl'
import turf from 'turf'
import { IRoute } from 'utils/interfaces'

/** Join consecutive 2-point GPS segments so turf.buffer is O(n), not a JSTS union of hundreds of polygons. */
const mergeMultiLineString = (geometry: any): any => {
  if (!geometry || !geometry.coordinates || !geometry.coordinates.length) return geometry
  if (geometry.type !== 'MultiLineString') return geometry

  const lines: any[][] = geometry.coordinates
  if (lines.length <= 1) return geometry

  const mergedLines: any[][] = []
  let currentLine: any[] = [...lines[0]]

  for (let i = 1; i < lines.length; i++) {
    const nextLine = lines[i]
    if (!nextLine || !nextLine.length) continue

    const lastPt = currentLine[currentLine.length - 1]
    const firstPt = nextLine[0]

    if (lastPt && firstPt && Math.abs(lastPt[0] - firstPt[0]) < 1e-8 && Math.abs(lastPt[1] - firstPt[1]) < 1e-8) {
      currentLine.push(...nextLine.slice(1))
    } else {
      mergedLines.push(currentLine)
      currentLine = [...nextLine]
    }
  }
  if (currentLine.length) {
    mergedLines.push(currentLine)
  }

  return {
    ...geometry,
    type: 'MultiLineString',
    coordinates: mergedLines,
  }
}

const ActiveRouteBufferOnMap = ({ tempRoute }: { tempRoute: IRoute | undefined }) => {
  const cleaningRouteBufferData: FeatureCollection | null = useMemo(() => {
    if (!tempRoute) return null
    const hasForward = !!tempRoute.properties?.forwardGeometry?.coordinates?.length
    const hasBackward = !!tempRoute.properties?.backwardGeometry?.coordinates?.length

    let rawGeometry: any = null
    if (hasForward) {
      rawGeometry = tempRoute.properties.forwardGeometry
    } else if (!hasBackward) {
      rawGeometry = tempRoute.geometry
    }

    if (!rawGeometry?.coordinates?.length) return null

    // Need at least 2 points for a valid line to buffer
    const coords = rawGeometry.coordinates
    const hasEnoughPoints =
      coords.some((line: any) => (Array.isArray(line) ? line.length >= 2 : false)) ||
      (Array.isArray(coords[0]) && !Array.isArray(coords[0][0]) && coords.length >= 2)
    if (!hasEnoughPoints) return null
    try {
      const featureToBuffer: any = {
        type: 'Feature',
        geometry: mergeMultiLineString(rawGeometry),
        properties: tempRoute.properties,
      }
      const buffered = turf.buffer(featureToBuffer, 1.2 / 2, 'meters')
      if (!buffered) return null
      return {
        type: 'FeatureCollection',
        features: [buffered],
      }
    } catch (e) {
      console.warn('[ActiveRouteBufferOnMap] turf.buffer failed:', e)
      return null
    }
  }, [tempRoute])

  const cleaningRouteBufferStyle: any = useMemo(() => {
    return {
      id: 'gl-draw-active-route-buffer',
      type: 'fill',
      paint: {
        'fill-color': '#f97316', // orange to distinguish editing buffer from saved (blue) routes
        'fill-opacity': 0.3,
      },
    }
  }, [])

  if (!cleaningRouteBufferData) return null

  return (
    <Source id="active-route-buffer" type="geojson" data={cleaningRouteBufferData}>
      {/* No beforeId — render on top of everything including draw vertices */}
      <Layer {...cleaningRouteBufferStyle} />
    </Source>
  )
}

export default ActiveRouteBufferOnMap
